import { ReactNode, useState } from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import Button from 'components/Common/Button';
import { useGetBusinessAddons } from '@/apis/business/addons/hook';
import { BusinessAddon } from '@/apis/business/addons/types';
import {
  AddonPriceBadgePiece,
  getAddonDisplayTitle,
  getAddonIcon,
  getAddonPriceBadgePieces,
  getAddonShortBlurb
} from '@/pages/Business/Setting/Addons/components/addonDisplay';
import ActivateAddonDrawer from '@/pages/Business/Setting/Addons/components/ActivateAddonDrawer';
import SmsEligibilityDrawer from '@/pages/Business/Setting/Addons/components/SmsEligibilityDrawer';
import { TelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/types';
import { showA2pAddOn, showHostSmsAddOn } from '../numberDetailFields';
import { usePhoneNumberAddonAccess } from '../hooks/usePhoneNumberAddonAccess';
import HostSmsTab from './HostSmsTab';
import A2pCampaignTab from './A2pCampaignTab';

function AddonPriceBadgeLabel({ pieces }: { pieces: AddonPriceBadgePiece[] }) {
  const theme = useTheme();
  const amountColor = theme.palette.primary.dark;
  const unitColor = theme.palette.primary.main;

  return (
    <Box component='span' sx={{ display: 'inline', fontSize: 12, lineHeight: 1.4, color: unitColor }}>
      {pieces.map((piece, index) => {
        if (piece.type === 'dot') {
          return (
            <Box component='span' key={`dot-${index}`} sx={{ fontWeight: 400 }}>
              {' · '}
            </Box>
          );
        }
        if (piece.type === 'text') {
          return (
            <Box component='span' key={`text-${index}`} sx={{ fontWeight: 400 }}>
              {piece.value}
            </Box>
          );
        }
        const emphasize = piece.emphasizeAmount !== false;
        return (
          <Box component='span' key={`seg-${index}`}>
            <Box
              component='span'
              sx={{ color: emphasize ? amountColor : unitColor, fontWeight: emphasize ? 700 : 400 }}
            >
              {piece.amount}
            </Box>
            <Box component='span' sx={{ color: unitColor, fontWeight: 400 }}>
              {piece.unit}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function StatusBadge({ label, tone }: { label: string; tone: 'active' | 'inactive' | 'warning' }) {
  const styles =
    tone === 'active'
      ? { bgcolor: '#DCFCE7', color: '#15803D' }
      : tone === 'warning'
        ? { bgcolor: '#FFEDD5', color: '#EA580C' }
        : { bgcolor: '#F3F4F6', color: '#6B7280' };

  return (
    <Chip
      label={label}
      size='small'
      sx={{
        ...styles,
        height: 'auto',
        minHeight: 22,
        fontSize: 11,
        fontWeight: 600,
        borderRadius: '999px',
        width: 'fit-content',
        py: 0.25,
        '& .MuiChip-label': { px: 1.25, py: 0.125 }
      }}
    />
  );
}

function PriceChip({ addon }: { addon: BusinessAddon }) {
  const theme = useTheme();
  const pieces = getAddonPriceBadgePieces(addon);
  if (!pieces.length) return null;

  return (
    <Chip
      label={<AddonPriceBadgeLabel pieces={pieces} />}
      size='small'
      sx={{
        bgcolor: theme.palette.primary.light,
        borderRadius: '100px',
        height: 'auto',
        py: 0.75,
        flexShrink: 0,
        '& .MuiChip-label': {
          whiteSpace: 'normal',
          px: 1.5,
          py: 0.25,
          textAlign: 'left'
        }
      }}
    />
  );
}

function AddonCardFrame({
  addon,
  status,
  extraStatus,
  action,
  children
}: {
  addon: BusinessAddon;
  status: { label: string; tone: 'active' | 'inactive' | 'warning' };
  extraStatus?: { label: string; tone: 'active' | 'inactive' | 'warning' } | null;
  action?: ReactNode;
  children?: ReactNode;
}) {
  const description = getAddonShortBlurb(addon);
  const billedNote = addon.code === 'sms_hosting' ? ' Billed per hosted number.' : '';

  return (
    <Box bgcolor='#fff' border='1px solid #E5E7EB' borderRadius='8px' p={2.5} mb={2.5}>
      <Box display='flex' gap={1.5} alignItems='flex-start'>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: '#E8F1FF',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { width: 22, height: 22 }
          }}
        >
          {getAddonIcon(addon.code)}
        </Box>
        <Box minWidth={0} flex={1}>
          {/* Title row: title left · price + Activate top-right (original price-badge slot) */}
          <Box display='flex' alignItems='center' justifyContent='space-between' gap={1.5} flexWrap='wrap'>
            <Typography fontWeight={700} fontSize={16} color='#111827' lineHeight={1.3}>
              {getAddonDisplayTitle(addon)}
            </Typography>
            <Box display='flex' alignItems='center' gap={1.5} flexWrap='wrap' flexShrink={0}>
              <PriceChip addon={addon} />
              {action}
            </Box>
          </Box>
          <Box display='flex' alignItems='center' gap={0.75} flexWrap='wrap' mt={0.75}>
            <StatusBadge label={status.label} tone={status.tone} />
            {extraStatus ? <StatusBadge label={extraStatus.label} tone={extraStatus.tone} /> : null}
          </Box>
          {description && (
            <Typography fontSize={13} color='#6B7280' mt={1} lineHeight={1.5} maxWidth={640}>
              {description}
              {billedNote}
            </Typography>
          )}
        </Box>
      </Box>
      {children ? <Box mt={2.25}>{children}</Box> : null}
    </Box>
  );
}

type Props = {
  preferredEndpoint?: TelephonyEndpoint | null;
};

export default function TelephonyAddOns({ preferredEndpoint = null }: Props) {
  const { isLoading, smsHostingPresent, smsHostingActive, a2pPresent, a2pActive } = usePhoneNumberAddonAccess();
  const { data: addonsData } = useGetBusinessAddons();
  const addons = addonsData?.data ?? [];
  const smsHostingAddon = addons.find((addon) => addon.code === 'sms_hosting') ?? null;
  const a2pAddon = addons.find((addon) => addon.code === 'a2p_10dlc') ?? null;

  const [activateAddon, setActivateAddon] = useState<BusinessAddon | null>(null);
  const [smsEligibilityAddon, setSmsEligibilityAddon] = useState<BusinessAddon | null>(null);
  const [hostSmsEmpty, setHostSmsEmpty] = useState(true);

  // SIP CA → Host SMS only; SIP US → Host SMS + A2P. Non-SIP keeps US / capability rules.
  const allowHostSms = showHostSmsAddOn(preferredEndpoint);
  const allowA2p = showA2pAddOn(preferredEndpoint);
  const showHostSmsCard = allowHostSms && smsHostingPresent && Boolean(smsHostingAddon);
  const showA2pCard = allowA2p && a2pPresent && Boolean(a2pAddon);

  const startActivate = (addon: BusinessAddon) => {
    if (addon.code === 'sms_hosting') {
      setSmsEligibilityAddon(addon);
      return;
    }
    setActivateAddon(addon);
  };

  if (isLoading) {
    return (
      <Box>
        <Typography fontWeight={700} fontSize={20} color='#111827' mb={2}>
          Add-Ons
        </Typography>
        <Typography fontSize={14} color='text.secondary'>
          Loading add-ons…
        </Typography>
      </Box>
    );
  }

  if (!showHostSmsCard && !showA2pCard) {
    return null;
  }

  return (
    <Box>
      <Typography fontWeight={700} fontSize={20} color='#111827' mb={2}>
        Add-Ons
      </Typography>

      {showHostSmsCard &&
        smsHostingAddon &&
        (smsHostingActive ? (
          <AddonCardFrame
            addon={smsHostingAddon}
            status={{ label: 'Activated', tone: 'active' }}
            extraStatus={hostSmsEmpty ? { label: 'SMS Hosting Not Done', tone: 'warning' } : null}
          >
            <HostSmsTab embedded preferredEndpoint={preferredEndpoint} onEmptyChange={setHostSmsEmpty} />
          </AddonCardFrame>
        ) : (
          <AddonCardFrame
            addon={smsHostingAddon}
            status={{ label: 'Not Activated', tone: 'inactive' }}
            action={
              <Button
                variant='contained'
                color='primary'
                onClick={() => startActivate(smsHostingAddon)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '999px',
                  px: 2.75,
                  minHeight: 40,
                  whiteSpace: 'nowrap'
                }}
              >
                Activate
              </Button>
            }
          />
        ))}

      {showA2pCard &&
        a2pAddon &&
        (a2pActive ? (
          <AddonCardFrame addon={a2pAddon} status={{ label: 'Activated', tone: 'active' }}>
            <A2pCampaignTab embedded />
          </AddonCardFrame>
        ) : (
          <AddonCardFrame
            addon={a2pAddon}
            status={{ label: 'Not Activated', tone: 'inactive' }}
            action={
              <Button
                variant='contained'
                color='primary'
                onClick={() => startActivate(a2pAddon)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '999px',
                  px: 2.75,
                  minHeight: 40,
                  whiteSpace: 'nowrap'
                }}
              >
                Activate
              </Button>
            }
          />
        ))}

      <SmsEligibilityDrawer
        addon={smsEligibilityAddon}
        open={Boolean(smsEligibilityAddon)}
        onClose={() => setSmsEligibilityAddon(null)}
        onContinue={() => {
          if (!smsEligibilityAddon) return;
          const next = smsEligibilityAddon;
          setSmsEligibilityAddon(null);
          setActivateAddon(next);
        }}
      />

      <ActivateAddonDrawer addon={activateAddon} open={Boolean(activateAddon)} onClose={() => setActivateAddon(null)} />
    </Box>
  );
}
